/**
 * lib/yearEnd.ts
 * ------------------------------------------------------------------
 * Closing the financial year.
 *
 * PURE. Journals in, closing entry out. No Firestore and no clock.
 *
 * WHAT CLOSING ACTUALLY IS. Income and expense accounts describe ONE YEAR —
 * "what did the school earn and spend between these two dates". Assets,
 * liabilities and the accumulated fund describe a MOMENT — "what does the
 * school own and owe today" — and they carry straight over. So at year end
 * every income and expense account is emptied into a single figure, that
 * figure is the surplus or deficit for the year, and it joins the fund.
 *
 * Without it, next year's income statement silently includes this year's,
 * and the further the school gets from the cut-over the less any report
 * means. It is not bookkeeping ceremony; it is what makes "this year" a
 * thing the accounts can express at all.
 *
 * TWO JOURNALS, NOT ONE. The first zeroes every income and expense account
 * into Surplus / (Deficit) for the Year. The second moves that surplus into
 * the Accumulated Fund and leaves the surplus account at nil, ready for the
 * next year. Keeping them apart means the year's result is legible as its
 * own entry — an auditor can read one journal and see the whole income
 * statement — instead of being buried inside a hundred-line transfer.
 *
 * IT IS COMPUTED FROM THE JOURNALS, never from a stored total. The surplus
 * is whatever the year's postings actually add up to, worked out the same
 * way the trial balance works it out. A stored figure and the journals it
 * claims to summarise drift, and the stored one is the one that lies
 * convincingly.
 *
 * CLOSING A YEAR THAT DOES NOT BALANCE IS REFUSED. If the trial balance for
 * the year is out, the closing entry would carry the difference into the
 * accumulated fund and make it permanent — the one place an error stops
 * being findable.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';
import type { GlAccount, GlJournal, JournalLine } from '@/lib/gl';

export interface YearEndAccounts {
  /** SURPLUS_CURRENT — 3200. Holds the year's result, briefly. */
  surplus: string;
  /** ACCUM_FUND — 3100. Where every past year's result has ended up. */
  accumulatedFund: string;
}

/** One account's net movement for the year, in debit-minus-credit terms. */
export interface YearEndRow {
  accountCode: string;
  name: string;
  type: GlAccount['type'];
  /** Debits less credits. Expenses come out positive, income negative. */
  net: number;
}

export interface YearEndPosition {
  fy: string;
  /** Every income and expense account that moved. */
  rows: YearEndRow[];
  /** Gross income for the year, as a positive figure. */
  totalIncome: number;
  /** Gross expenditure for the year, as a positive figure. */
  totalExpense: number;
  /** Income less expenditure. Negative is a deficit. */
  surplus: number;
  /** Debits less credits across EVERY account. Must be nil to close. */
  outOfBalance: number;
  /** Journals in the year that have already been reversed — excluded. */
  reversedCount: number;
  /** True once a closing journal for this year exists. */
  alreadyClosed: boolean;
}

export function yearEndSourceId(fy: string): string {
  return `close-${fy}`;
}

export function yearEndTransferSourceId(fy: string): string {
  return `close-${fy}-fund`;
}

/**
 * What the year comes to.
 *
 * A REVERSED JOURNAL AND ITS REVERSAL BOTH COUNT. They cancel to nil by
 * construction, so excluding either would leave the other stranded and the
 * year out of balance. `reversedCount` is reported so a bursar can see that
 * the year contains corrections, which is information, not a problem.
 */
export function buildYearEndPosition(
  journals: GlJournal[],
  accounts: GlAccount[],
  fy: string,
): YearEndPosition {
  const byCode = new Map(accounts.map((a) => [a.code, a]));
  const net = new Map<string, number>();
  let outOfBalance = 0;
  let reversedCount = 0;
  let alreadyClosed = false;

  for (const j of journals) {
    if (j.fy !== fy) continue;
    if (j.source?.type === 'year_end') { alreadyClosed = true; continue; }
    if (j.status === 'reversed') reversedCount += 1;
    for (const l of j.lines ?? []) {
      const delta = round2((Number(l.dr) || 0) - (Number(l.cr) || 0));
      net.set(l.accountCode, round2((net.get(l.accountCode) ?? 0) + delta));
      outOfBalance = round2(outOfBalance + delta);
    }
  }

  const rows: YearEndRow[] = [];
  let totalIncome = 0;
  let totalExpense = 0;

  for (const [code, amount] of net) {
    const acc = byCode.get(code);
    if (!acc || (acc.type !== 'income' && acc.type !== 'expense')) continue;
    if (round2(amount) === 0) continue;
    rows.push({ accountCode: code, name: acc.name, type: acc.type, net: round2(amount) });
    // Income sits on the credit side, so its net is negative; flip it to read
    // as a positive figure. Contra-revenue (4900 Discounts Granted) is typed
    // income and carries a DEBIT balance, so it correctly reduces the total.
    if (acc.type === 'income') totalIncome = round2(totalIncome - amount);
    else totalExpense = round2(totalExpense + amount);
  }

  rows.sort((a, b) => a.accountCode.localeCompare(b.accountCode));

  return {
    fy,
    rows,
    totalIncome,
    totalExpense,
    surplus: round2(totalIncome - totalExpense),
    outOfBalance: round2(outOfBalance),
    reversedCount,
    alreadyClosed,
  };
}

/**
 * The closing entry: every income and expense account back to nil.
 *
 * Each account is posted the OPPOSITE way to how it stands, so it ends at
 * zero, and the balancing figure lands in Surplus for the Year. Listing
 * every account rather than posting two summary lines is deliberate: the
 * journal then IS the income statement, and an auditor reading it does not
 * have to reconstruct where the number came from.
 */
export function yearEndClosingLines(
  position: YearEndPosition,
  accounts: YearEndAccounts,
): JournalLine[] {
  const lines: JournalLine[] = position.rows.map((r) => ({
    accountCode: r.accountCode,
    dr: r.net < 0 ? round2(-r.net) : 0,
    cr: r.net > 0 ? r.net : 0,
    narration: `Closed to surplus — ${position.fy}`,
  }));

  if (lines.length === 0) return [];

  // The surplus account takes whatever the other side comes to.
  const dr = round2(lines.reduce((a, l) => a + l.dr, 0));
  const cr = round2(lines.reduce((a, l) => a + l.cr, 0));
  const balancing = round2(dr - cr);
  if (balancing !== 0) {
    lines.push({
      accountCode: accounts.surplus,
      dr: balancing < 0 ? round2(-balancing) : 0,
      cr: balancing > 0 ? balancing : 0,
      narration: position.surplus >= 0
        ? `Surplus for ${position.fy}`
        : `Deficit for ${position.fy}`,
    });
  }

  return lines;
}

/**
 * The second entry: the year's result joins the fund.
 *
 * Separate from the closing entry so the year's result is readable on its
 * own, and so the surplus account starts the next year at nil rather than
 * accumulating every year the school has ever had — which is the
 * accumulated fund's job, and the reason the two accounts are different.
 */
export function yearEndTransferLines(
  position: YearEndPosition,
  accounts: YearEndAccounts,
): JournalLine[] {
  const s = round2(position.surplus);
  if (s === 0) return [];

  const narration = s > 0
    ? `Surplus for ${position.fy} to accumulated fund`
    : `Deficit for ${position.fy} to accumulated fund`;

  // A surplus stands as a CREDIT in the surplus account, so clearing it is a
  // debit; the fund is credited. A deficit is the mirror.
  return s > 0
    ? [
      { accountCode: accounts.surplus, dr: s, cr: 0, narration },
      { accountCode: accounts.accumulatedFund, dr: 0, cr: s, narration },
    ]
    : [
      { accountCode: accounts.accumulatedFund, dr: round2(-s), cr: 0, narration },
      { accountCode: accounts.surplus, dr: 0, cr: round2(-s), narration },
    ];
}

/**
 * Why this year cannot be closed, or null if it can.
 *
 * The out-of-balance check is the important one. Closing sweeps the
 * difference into the accumulated fund, where it stops being an error in a
 * month somebody can find and becomes part of the school's opening position
 * forever.
 */
export function yearEndProblem(position: YearEndPosition): string | null {
  if (position.alreadyClosed) {
    return `${position.fy} has already been closed. Re-closing would double the transfer to the fund.`;
  }
  if (position.rows.length === 0) {
    return `Nothing was posted to an income or expense account in ${position.fy}.`;
  }
  if (Math.abs(position.outOfBalance) > 0.005) {
    return `${position.fy} is out of balance by ${Math.abs(position.outOfBalance).toFixed(2)}. `
      + 'Closing would carry that difference into the accumulated fund, where it stops being '
      + 'findable. Fix the year first.';
  }
  return null;
}
